import createStructuredDateService from './structuredDateService';
import createStructuredMonthService from './structuredMonthService';
import createStructuredYearService from './structuredYearService';
import { DateLocalization, DateStringFormatter, StructuredDate, StructuredMonth, StructuredYear } from './types';

export interface CalendarService<D = StructuredDate, M = StructuredMonth, Y = StructuredYear> {
    // date
    isDateLess: (d1: D, d2: D) => boolean;
    isDateEqual: (d1: D, d2: D) => boolean;
    isDateGreater: (d1: D, d2: D) => boolean;
    isDateGreaterOrEqual: (d1: D, d2: D) => boolean;
    isDateLessOrEqual: (d1: D, d2: D) => boolean;
    isDateBetween: (date: D, start: D, end: D) => boolean;
    isDateBetweenOrEqual: (date: D, start: D, end: D) => boolean;
    isDateDisabled: (date: D, endDates: { minDate?: D; maxDate?: D }) => boolean;
    getOrderedRange: (date1: D, date2: D) => [D, D];
    addDays: (date: D, count: number) => D;
    addBusinessDays: (date: D, count: number) => D;
    getPrevDate: (date: D) => D;
    getNextDate: (date: D) => D;
    getPrevWeek: (date: D) => D;
    getNextWeek: (date: D) => D;
    getMinDate: (dates: Array<D>) => D;
    getMaxDate: (dates: Array<D>) => D;
    getDayNames: () => string[]; // deprecated
    getShortDayNames: () => string[];
    getFullDayNames: () => string[];
    getWeekdayIndex: (date: D) => number;
    getWeekday: (date: D) => string;
    getCurrentDate: () => D;
    getDateNumber: (date: D) => number;
    getDateFromDateString: (dateString: string) => D;
    getDateString: (date: D) => string;
    getFormattedDateString?: (date: D, dateFormat: string) => string;
    isValidDateString: (dateString: string) => boolean;
    toISOString: (date: D) => string;
    fromISOString: (isoString: string) => D;
    getDiffNights: (d1: D, d2: D) => number;

    // month
    addMonths: (month: M, count: number) => M;
    getMonthOfDate: (date: D) => M;
    getMonthFirstDate: (month: M) => D;
    getMonthLastDate: (month: M) => D;
    subtractMonths: (month: M, count: number) => M;
    getNextMonth: (month: M) => M;
    getPrevMonth: (month: M) => M;
    isMonthDisabled: (nextMonth: M, allowedRange: { minDate?: D; maxDate?: D }) => boolean;
    isFutureMonthDisabled: (nextMonth: M, allowedRange: { minDate?: D; maxDate?: D }) => boolean;
    isPastMonthDisabled: (nextMonth: M, allowedRange: { minDate?: D; maxDate?: D }) => boolean;
    isMonthEqual: (month1: M, month2: M) => boolean;
    isMonthGreater: (month1: M, month2: M) => boolean;
    isMonthLess: (month1: M, month2: M) => boolean;
    diffMonths: (month1: M, month2: M) => number;
    getMonthIndex: (month: M) => number;
    getMonthName: (month: M) => string;
    getMonthShortName: (month: M) => string;
    getMonthShortNames: () => Array<string>;
    getMonthUniqueName: (month: M) => string;
    getMonthYearString: (month: M) => string;
    getDateStringFromMonthYearString: (dateString: string, dateFormatPattern: string) => string;
    getWeeksOfMonth: (month: M) => Array<Array<D | null>>;

    // year
    addYears: (year: Y, count: number) => Y;
    getYearOfMonth: (month: M) => Y;
    getMonthsOfYear: (year: Y) => M[];
    getYearName: (year: Y) => number;
}

export interface CalendarServiceFactory<T = StructuredDate> {
    getLocalization: () => DateLocalization;
    getCurrentDate: () => T;
    getDateString: (date: T) => string;
    getFormattedDateString?: (date: T, format: string) => string;
    isValidDateString: (dateString: string) => boolean;
    fromDateString: (dateString: string) => T;
    parseDateFromFormat: (dateString: string, format: string, locale?: string) => DateStringFormatter;
}

export default ({
    isValidDateString: passedIsValidDateString,
    getCurrentDate: passedGetCurrentDate,
    getDateString: passedGetDateString,
    getFormattedDateString,
    getLocalization,
    fromDateString,
    parseDateFromFormat,
}: CalendarServiceFactory): CalendarService => {
    const {
        addDays,
        addBusinessDays,
        getPrevDate,
        getNextDate,
        getPrevWeek,
        getNextWeek,
        getMinDate,
        getMaxDate,
        getDayNames, // deprecated
        getShortDayNames,
        getFullDayNames,
        getWeekdayIndex,
        getWeekday,
        getCurrentDate,
        getDateNumber,
        getDateFromDateString,
        getDateString,
        isValidDateString,
        isDateLess,
        isDateEqual,
        isDateBetween,
        isDateBetweenOrEqual,
        isDateGreater,
        isDateDisabled,
        getOrderedRange,
        isDateLessOrEqual,
        isDateGreaterOrEqual,
        toISOString,
        fromISOString,
        getDiffNights,
    } = createStructuredDateService({
        isValidDateString: passedIsValidDateString,
        getCurrentDate: passedGetCurrentDate,
        getDateString: passedGetDateString,
        getLocalization,
        fromDateString,
    });

    return {
        addDays,
        addBusinessDays,
        getPrevDate,
        getNextDate,
        getPrevWeek,
        getNextWeek,
        getMinDate,
        getMaxDate,
        getDayNames, // deprecated
        getShortDayNames,
        getFullDayNames,
        getWeekdayIndex,
        toISOString,
        getWeekday,
        getCurrentDate,
        getDateNumber,
        getDateFromDateString,
        getDateString,
        getFormattedDateString,
        isValidDateString,
        isDateLess,
        isDateEqual,
        isDateBetween,
        isDateBetweenOrEqual,
        isDateGreater,
        isDateDisabled,
        getOrderedRange,
        isDateLessOrEqual,
        isDateGreaterOrEqual,
        fromISOString,
        getDiffNights,
        ...createStructuredMonthService({
            getLocalization,
            parseDateFromFormat,
        }),
        ...createStructuredYearService(),
    };
};
